define([
	"dojo/_base/declare",
	"dojo/dom-class",
	"designSystem/types/button-types",
	"csi/_base/underscore",
	"csi/action/Controller",
	"csi/html",
	"csi/locale"
], function(declare, domClass, {ButtonEnum}, _, Controller, html, locale) {

	const {
		BUTTON_CANCEL_ALL,
		BUTTON_PREV_PAGE,
		BUTTON_NEXT_PAGE,
		BUTTON_FINISH,
		BUTTON_FINISH_GO,
		BUTTON_SAVE_NEW,
	} = ButtonEnum;

	// Additional Controller functions for actions using wizard view
	return declare(null, {
		// Extension points to override based on wizard logic
		WIZARD_SKIP_INDEX: -1,

		// String: resource to use to get wizard titles/messages (defaults to action Id)
		//	if specified, then add resources <idWizardResource>_Title<Index> for each page index, and optionally for <idWizardResource>_Detail<Index>
		//idWizardResource: null,

		// Number[]: specify which page(s) of wizard to skip (page indices start at 0)
		//wizardSkipPages: null

		/**
		 * @property {boolean} isCopyingNode - indicates if in the middle of copying nodes, it refers to the "New From" case for now
		 *  if true, then we would show the summary indicator at the top of left pane
		 *  and also,  show the action if configured for the current round of the process(there would be mass operation if user selects multiple nodes)
		 */
		isCopyingNode: false,

		disableFinish: function(/*csi.wizard.Widget*/wizard, /*Content*/page, /*Number*/index) {
			// summary: return true to disable Finish button - by default it is always enabled if shown
			return false;
		},

		/**
		 * return true to disable Next button - by default, it is hidden on last page so
		 */
		disableNext: function(wizard, page, index) {
			return false;
		},

		disablePrev: function(/*csi.wizard.Widget*/wizard, /*Content*/page, /*Number*/index) {
			// summary: return true to disable Previous button - by default, it is always disabled on first page
			return false;
		},

		/**
		 * get the step action for each process in the case of copy case("New From")
		 * @param {Widget} wizard 
		 * @param {Node} node - the current node
		 */
		getWizardStepAction: function(wizard, node) {
			return null;
		},

		getWizardViewQuery: function(/*csi.wizard.Widget*/wizard, /*csi.view.Widget*/view, /*Number*/index) {
			// summary: return Query filter for given page view
			return null;
		},

		/**
		 * notify when done with navigation to this page
		 * @param {Widget} wizard
		 * @param {Widget} currentPage 
		 */
		onPostNextPage: function(wizard, currentPage) { },

		onWizardViewPreCreate: function(/*csi.wizard.Widget*/wizard, /*Content*/page, /*View*/defView, /*Object*/viewParams) {
			// summary: notify when view definition for page is found and ready for creation with ability to add own viewParams
		},

		onWizardViewCreate: function(/*csi.wizard.Widget*/wizard, /*Content*/page, /*csi.view.Widget*/view) {
			// summary: notify when view created for page
		},

		onNextPage: function(/*csi.wizard.Widget*/wizard, /*Content*/currentPage, /*Content*/nextPage, /*Boolean*/bFinish) {
			// summary: called when moving to next page or finishing last page; returns Deferred
			return wizard.carryOverFormData(currentPage, nextPage);
		},

		onPrevPage: function(/*csi.wizard.Widget*/wizard, /*Content*/currentPage, /*Content*/prevPage) {
			// summary: called when moving to previous page; returns Deferred
			return null;
		},

		// Internal wizard controller functions

		addProxyActions: function(/*View*/defView, /*Actions*/defActions) {
			// summary: add standard wizard actions
			if (defView.getType() == "Wizard") {

				defActions.addAction(this.getProxyAction(BUTTON_PREV_PAGE));
				defActions.addAction(this.getProxyAction(BUTTON_NEXT_PAGE, false, {m_class: defView.bShowFinishButton ? null : html.CSS_BUTTON_PRIMARY}));

				const {_sourceSelectedLength} = this;

				// in the case of "New From", it only makes sense to enable the "Save & Go" button when just select one node
				if (this._canNavigate && this._canNavigate() && (_sourceSelectedLength || 0) < 2) {
					defActions.addAction(this.getProxyAction(BUTTON_FINISH_GO, true));
				}

				if (this._canRepeat()) {
					defActions.addAction(this.getProxyAction(BUTTON_SAVE_NEW, true, {m_bRepeat: true}));
				}

				defActions.addAction(this.getProxyAction(BUTTON_FINISH, false, {}));

				if (_sourceSelectedLength) {
					defActions.addAction(this.getProxyAction(BUTTON_CANCEL_ALL, false, {
						m_name: locale.resource("Cancel"),
						m_class: html.CSS_BUTTON_LINK
					}));
				}

				Controller.prototype.addProxyActions.call(this, defView, defActions);
			} else {
				this.inherited(arguments);
			}
		},

		getWizardNumberOfRealizedPages: function(/*csi.wizard.Widget*/wizard) {
			// summary: override when wizard skips pages based on certain conditions and return the number of pages in wizard (must be constant)
			return wizard.getPages().length - (this.wizardSkipPages ? this.wizardSkipPages.length : 0);
		},

		getWizardRealizedPageIndex: function(/*csi.wizard.Widget*/wizard, /*Number*/index) {
			// summary: override when wizard skips pages based on certain conditions and return the page index as seen by the user - return WIZARD_SKIP_INDEX if index will be skipped
			if (this.wizardSkipPages) {
				if (_.contains(this.wizardSkipPages, index)) {
					return this.WIZARD_SKIP_INDEX;
				}

				index = _.reduce(this.wizardSkipPages, function(memo, skipIndex) {
					return (index > skipIndex) ? (memo - 1) : memo;
				}, index);
			}

			return index;
		},

		onWizardPageSelect: function(/*csi.wizard.Widget*/wizard, /*Content*/page) {
			// summary: add detail message to dialog
			var index = wizard.getIndexOfPage(page);

			var pages = wizard.getPages();
			var finishButton = wizard.getActionButton("Finish");
			if (finishButton) {
				// check if this is last page, then set finish button as primary otherwise set as secondary
				domClass.toggle(finishButton, html.CSS_BUTTON_PRIMARY, (index + 1) == pages.length);
				domClass.toggle(finishButton, html.CSS_BUTTON_SECONDARY, (index + 1) != pages.length);
			}
		},

		/**
		 * @override
		 * Avoid messing wizards up, return false for now
		 */
		_canRepeat: function() {
			// TBD:  need to adjust it after resolving all the wizards
			return false;
		},

		/**
		 * @override
		 * Add dialog class for wizards
		 * @param {Object} dialogParams
		 */
		_onCreatePopupDialogParams: function(dialogParams) {
			this.inherited(arguments);
			dialogParams["class"] = "csi-wizard-view-dialog " + (dialogParams["class"] || "");
		}
	});
});